/**
 * Mouse handling for chart drawings: place, select, drag, delete.
 *
 * Uses raw DOM listeners rather than the chart's own `subscribeClick`, because
 * lightweight-charts exposes clicks and crosshair moves but not drag. Reshaping
 * a trendline needs mousedown -> mousemove -> mouseup as one gesture.
 *
 * The critical detail is disabling `handleScroll`/`handleScale` for the
 * duration of a drag. Without it the chart pans under the cursor while you move
 * a point, so the anchor chases the mouse and the line never lands where you
 * put it.
 */
import { useEffect, useRef } from 'react';
import type { IChartApi } from 'lightweight-charts';
import type { DrawingPrimitive } from '../components/Chart/DrawingPrimitive';
import { useDrawingStore } from '../store/drawingStore';
import { handleAt, isOnDrawing, ANCHOR_COUNT } from '../components/Chart/drawingGeometry';

interface DragState {
  id: string;
  /** Handle index being dragged, or -1 when moving the whole drawing. */
  handleIndex: number;
  /** Pointer position at mousedown, for whole-drawing moves. */
  startX: number;
  startY: number;
  startAnchors: { time: number; price: number }[];
}

export function useDrawingInteraction(
  container: HTMLDivElement | null,
  chart: IChartApi | null,
  primitive: DrawingPrimitive | null,
  symbol: string
): void {
  const drag = useRef<DragState | null>(null);
  // Chart options are restored from here rather than assumed, so a drag that
  // starts while the user already has scrolling off does not silently turn it
  // back on.
  const savedInteraction = useRef<{ scroll: boolean; scale: boolean } | null>(null);

  useEffect(() => {
    if (!container || !chart || !primitive) return;

    const localPoint = (e: MouseEvent): { x: number; y: number } => {
      const rect = container.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const freezeChart = (): void => {
      const opts = chart.options();
      savedInteraction.current = {
        scroll: opts.handleScroll !== false,
        scale: opts.handleScale !== false,
      };
      chart.applyOptions({ handleScroll: false, handleScale: false });
    };

    const thawChart = (): void => {
      const saved = savedInteraction.current;
      if (!saved) return;
      chart.applyOptions({ handleScroll: saved.scroll, handleScale: saved.scale });
      savedInteraction.current = null;
    };

    const onMouseDown = (e: MouseEvent): void => {
      if (e.button !== 0) return;
      const { x, y } = localPoint(e);
      const store = useDrawingStore.getState();
      const bounds = primitive.paneBounds();

      // Placing a new drawing takes priority: with a tool armed, a click is an
      // anchor, never a selection.
      if (store.activeTool) {
        const anchor = primitive.toAnchor(x, y);
        if (!anchor) return;
        store.addPendingAnchor(symbol, anchor);
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Selected drawing gets first refusal on its own handles, so overlapping
      // drawings don't steal the point you are already editing.
      const projected = primitive.projected();
      const ordered = store.selectedId
        ? [...projected].sort((a, b) =>
            a.drawing.id === store.selectedId ? -1 : b.drawing.id === store.selectedId ? 1 : 0
          )
        : projected;

      for (const { drawing, points } of ordered) {
        const hIdx = drawing.id === store.selectedId ? handleAt(points, x, y) : -1;
        const onBody = hIdx === -1 && isOnDrawing(drawing.tool, points, bounds, x, y);
        if (hIdx === -1 && !onBody) continue;

        store.select(drawing.id);
        drag.current = {
          id: drawing.id,
          handleIndex: hIdx,
          startX: x,
          startY: y,
          startAnchors: drawing.anchors.map((a) => ({ ...a })),
        };
        freezeChart();
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Empty space clears the selection, matching the trade-marker popup.
      if (store.selectedId) store.select(null);
    };

    const onMouseMove = (e: MouseEvent): void => {
      const d = drag.current;
      if (!d) return;
      const { x, y } = localPoint(e);
      const store = useDrawingStore.getState();

      if (d.handleIndex >= 0) {
        const anchor = primitive.toAnchor(x, y);
        if (!anchor) return;
        const anchors = d.startAnchors.map((a, i) => (i === d.handleIndex ? anchor : { ...a }));
        store.updateAnchors(symbol, d.id, anchors);
      } else {
        // Whole-drawing move: convert the pixel delta once at the cursor so the
        // shape keeps its geometry. Translating each anchor by a pixel offset
        // and re-projecting would distort it on a non-linear price scale.
        const from = primitive.toAnchor(d.startX, d.startY);
        const to = primitive.toAnchor(x, y);
        if (!from || !to) return;
        const dt = to.time - from.time;
        const dp = to.price - from.price;
        const anchors = d.startAnchors.map((a) => ({ time: a.time + dt, price: a.price + dp }));
        store.updateAnchors(symbol, d.id, anchors);
      }
      e.preventDefault();
    };

    const onMouseUp = (): void => {
      if (!drag.current) return;
      drag.current = null;
      thawChart();
    };

    const onKeyDown = (e: KeyboardEvent): void => {
      const store = useDrawingStore.getState();
      if (e.key === 'Escape') {
        if (store.pending.length > 0) store.cancelPending();
        else if (store.activeTool) store.setActiveTool(null);
        else store.select(null);
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && store.selectedId) {
        // Never steal the key from a form: a drawing stays selected while the
        // user types in the order ticket, and deleting it mid-sentence would be
        // baffling.
        const el = document.activeElement;
        const typing =
          el instanceof HTMLInputElement ||
          el instanceof HTMLTextAreaElement ||
          (el as HTMLElement | null)?.isContentEditable === true;
        if (typing) return;
        store.remove(symbol, store.selectedId);
        e.preventDefault();
      }
    };

    container.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      container.removeEventListener('mousedown', onMouseDown, true);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('keydown', onKeyDown);
      // A drag interrupted by unmount must not leave the chart frozen.
      thawChart();
    };
  }, [container, chart, primitive, symbol]);
}

/** Anchors still needed to finish the in-progress drawing. */
export function remainingAnchors(tool: keyof typeof ANCHOR_COUNT, placed: number): number {
  return Math.max(0, ANCHOR_COUNT[tool] - placed);
}
